// src/testMode/TestPanel.jsx
// Floating panel with one button per manual step. Rendered by App only in test mode.
import React, { useContext, useEffect, useState } from 'react';
import { useMatch, useNavigate } from 'react-router-dom';
import { UserContext } from '../context/UserContext';
import { signInAsTestUser, signOutUser } from '../auth';
import { isTestModeForced, notifyDataChanged } from './testMode';
import { DASHBOARD_STEPS, TOURNAMENT_STEPS, addPlayers, getTournaments } from './steps';

function TestPanel() {
    const user = useContext(UserContext);
    const navigate = useNavigate();
    const tournamentMatch = useMatch('/tournament/:tournamentId');
    const dashboardMatch = useMatch('/dashboard');
    // Collapsed by default on phones, where the expanded dock would take over the screen.
    const [collapsed, setCollapsed] = useState(
        () => typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 700px)').matches
    );
    const [busy, setBusy] = useState(false);
    const [log, setLog] = useState(null); // { ok: boolean, text: string }
    const [playerCountText, setPlayerCountText] = useState('8'); // raw text so the field can be cleared while typing
    const playerCount = Math.max(1, parseInt(playerCountText, 10) || 1);

    // Lets the page CSS keep its content clear of the dock.
    useEffect(() => {
        document.body.classList.add('has-test-panel');
        return () => document.body.classList.remove('has-test-panel', 'test-panel-collapsed');
    }, []);
    useEffect(() => {
        document.body.classList.toggle('test-panel-collapsed', collapsed);
    }, [collapsed]);

    // Runs one step; on success tells the pages to refetch their data.
    const run = async (action) => {
        setBusy(true);
        try {
            setLog({ ok: true, text: await action() });
            notifyDataChanged();
        } catch (error) {
            setLog({ ok: false, text: error.message });
        } finally {
            setBusy(false);
        }
    };

    const ctx = user ? { userId: user.uid, tournamentId: tournamentMatch?.params.tournamentId } : null;

    const handleSignIn = () => {
        signInAsTestUser();
        navigate('/dashboard');
    };

    const handleSignOut = async () => {
        await signOutUser();
        navigate('/');
    };

    const handleOpenLatestTournament = () =>
        run(async () => {
            const tournaments = await getTournaments(user.uid);
            const latest = tournaments[tournaments.length - 1];
            if (!latest) throw new Error('There are no tournaments');
            navigate(`/tournament/${latest.tournamentId}`);
            return `Opened ${latest.name}`;
        });

    const handleExit = () => {
        window.location.assign(`${window.location.pathname}?testMode=0`);
    };

    const stepButton = (step) => (
        <button key={step.id} className="btn" disabled={busy} onClick={() => run(() => step.run(ctx))}>
            {step.label}
        </button>
    );

    return (
        <div className="test-panel" data-testid="test-panel">
            <div className="test-panel-head">
                <strong>Test mode</strong>
                <span>{user ? `Signed in as ${user.displayName}` : 'Not signed in'}</span>
                {log && (
                    <span role="status" className={log.ok ? 'test-log-ok' : 'test-log-error'}>
                        {log.text}
                    </span>
                )}
                <button className="btn btn-sm test-panel-toggle" onClick={() => setCollapsed((prev) => !prev)}>
                    {collapsed ? 'Expand' : 'Collapse'}
                </button>
            </div>

            {!collapsed && (
                <div className="test-panel-buttons">
                    {!user && (
                        <button className="btn" onClick={handleSignIn}>
                            Sign in as test user
                        </button>
                    )}

                    {user && dashboardMatch && (
                        <>
                            {DASHBOARD_STEPS.map(stepButton)}
                            <button className="btn" disabled={busy} onClick={handleOpenLatestTournament}>
                                Open latest tournament
                            </button>
                        </>
                    )}

                    {user && tournamentMatch && (
                        <>
                            {TOURNAMENT_STEPS.map(stepButton)}
                            <div className="row">
                                <input
                                    className="input"
                                    type="number"
                                    min="1"
                                    aria-label="Number of players"
                                    value={playerCountText}
                                    onChange={(e) => setPlayerCountText(e.target.value)}
                                />
                                <button className="btn" disabled={busy} onClick={() => run(() => addPlayers(ctx, playerCount))}>
                                    Add {playerCount} random players
                                </button>
                            </div>
                            <button className="btn" onClick={() => navigate('/dashboard')}>
                                Back to dashboard
                            </button>
                        </>
                    )}

                    {user && (
                        <button className="btn" onClick={handleSignOut}>
                            Sign out
                        </button>
                    )}

                    {!isTestModeForced() && (
                        <button className="btn" onClick={handleExit}>
                            Exit test mode
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

export default TestPanel;
